
import React, { useEffect } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ContactForm from '@/components/ContactForm';
import { Headset, Clock, MapPin, MessageSquare, Star } from 'lucide-react';

const Contact = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Header */}
      <section className="pt-32 pb-20 bg-gradient-to-b from-pkearth-100 to-white relative">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <span className="text-pkearth-800 font-display uppercase tracking-wider text-sm mb-2 block animate-fade-up">
              Connect With Our Team
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold mb-6 text-gray-900 animate-fade-up">
              Let's Discuss Your Vision
            </h1>
            <p className="text-xl text-gray-700 animate-fade-up">
              Reach out to PK Landscape Associates to schedule a consultation
              or request a personalized quote for your landscape project.
            </p>
          </div>
        </div>
      </section>
      
      {/* How We Work Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-serif font-bold text-center mb-12">Our Consultation Process</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-pkgreen-50 p-8 rounded-lg text-center">
                <div className="bg-pkgreen-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="h-7 w-7 text-pkgreen-600" />
                </div>
                <h3 className="font-serif font-bold text-xl mb-3">1. Initial Consultation</h3>
                <p className="text-gray-700">
                  We'll discuss your vision, preferences, and requirements for your landscape project.
                </p>
              </div>
              
              <div className="bg-pkgreen-50 p-8 rounded-lg text-center">
                <div className="bg-pkgreen-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Headset className="h-7 w-7 text-pkgreen-600" />
                </div>
                <h3 className="font-serif font-bold text-xl mb-3">2. Site Assessment</h3>
                <p className="text-gray-700">
                  Our design team will evaluate your property to understand its unique characteristics.
                </p>
              </div>
              
              <div className="bg-pkgreen-50 p-8 rounded-lg text-center">
                <div className="bg-pkgreen-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="h-7 w-7 text-pkgreen-600" />
                </div>
                <h3 className="font-serif font-bold text-xl mb-3">3. Proposal & Execution</h3>
                <p className="text-gray-700">
                  We'll present a detailed proposal with designs and timeline for your project.
                </p>
              </div>
            </div>
            
            <div className="mt-12 text-center">
              <p className="text-lg text-gray-700">
                Typically, we respond to all inquiries within 24-48 hours during business days.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Contact Form */}
      <ContactForm />

      {/* Testimonial Section */}
      <section className="py-16 bg-pkearth-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <span className="text-pkearth-800 font-display uppercase tracking-wider text-sm mb-2 block">
              Client Feedback
            </span>
            <h2 className="text-3xl font-serif font-bold mb-12">What Our Clients Say</h2>
            
            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="flex justify-center mb-4">
                <div className="flex text-pkearth-500">
                  <Star className="fill-pkearth-500 h-5 w-5" />
                  <Star className="fill-pkearth-500 h-5 w-5" />
                  <Star className="fill-pkearth-500 h-5 w-5" />
                  <Star className="fill-pkearth-500 h-5 w-5" />
                  <Star className="fill-pkearth-500 h-5 w-5" />
                </div>
              </div>
              <blockquote className="text-lg text-gray-700 italic mb-6">
                "PK Landscape transformed our property into an elegant outdoor sanctuary. 
                Their design expertise and attention to detail exceeded our expectations. 
                The entire process was seamless from initial consultation to final installation."
              </blockquote>
              <div className="font-medium">
                <p className="text-pkearth-800">Elizabeth Westbrook</p>
                <p className="text-sm text-gray-600">Greenwich, CT</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-serif font-bold text-center mb-12">Frequently Asked Questions</h2>
            
            <div className="space-y-6">
              <div className="border-b border-gray-200 pb-6">
                <h3 className="text-xl font-serif font-bold mb-3">How soon can you start my project?</h3>
                <p className="text-gray-700">
                  Project timelines vary based on complexity and our current schedule. During your consultation, 
                  we'll provide a realistic timeline for your specific project. We typically schedule projects 
                  2-4 weeks in advance during peak season.
                </p>
              </div>
              
              <div className="border-b border-gray-200 pb-6">
                <h3 className="text-xl font-serif font-bold mb-3">Do you offer maintenance services after installation?</h3>
                <p className="text-gray-700">
                  Yes, we provide comprehensive maintenance services to keep your landscape looking its best. 
                  We can create a customized maintenance plan based on your property's specific needs.
                </p>
              </div>
              
              <div className="border-b border-gray-200 pb-6">
                <h3 className="text-xl font-serif font-bold mb-3">What areas of Connecticut do you service?</h3>
                <p className="text-gray-700">
                  We proudly serve Fairfield County including Greenwich, Stamford, Darien, Westport, 
                  Fairfield, and surrounding areas. For projects outside this area, please contact us 
                  to discuss availability.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default Contact;
